// piexifjs ist eine alte UMD-Library, deren default-Export bei Vite das
// Haupt-Objekt mit ImageIFD, ExifIFD usw. liefert (kein TagValues-Wrapper).
import piexif from "piexifjs";

type ExifIfdEntry = Record<number, unknown>;
type ExifDict = {
  "0th"?: ExifIfdEntry;
  Exif?: ExifIfdEntry;
  GPS?: ExifIfdEntry;
  Interop?: ExifIfdEntry;
  "1st"?: ExifIfdEntry;
  thumbnail?: string | null;
  [key: string]: ExifIfdEntry | string | null | undefined;
};

interface Piexif {
  load(jpegDataUrl: string): ExifDict;
  dump(exif: ExifDict): string;
  insert(exifBinary: string, jpegDataUrl: string): string;
  ImageIFD: {
    Orientation: number;
    XResolution: number;
    YResolution: number;
    ResolutionUnit: number;
  };
  ExifIFD: Record<string, number>;
  GPSIFD: Record<string, number>;
}

const PIEXIF = piexif as unknown as Piexif;

export function readJpegExif(jpegBytes: ArrayBuffer): {
  orientation: number;
  exifDict: ExifDict | null;
} {
  try {
    const binary = arrayBufferToBinaryString(jpegBytes);
    const dataUrl = `data:image/jpeg;base64,${btoa(binary)}`;
    const exifDict = PIEXIF.load(dataUrl);
    const zeroth = exifDict["0th"] ?? {};
    const orientation = (zeroth[PIEXIF.ImageIFD.Orientation] as number | undefined) ?? 1;
    return { orientation, exifDict };
  } catch {
    return { orientation: 1, exifDict: null };
  }
}

// Baut das EXIF für die Ausgabe (Kopie, das Original-Dict bleibt unverändert).
function buildExifDict(exifDict: ExifDict | null, dpi: number): ExifDict {
  const baseDict: ExifDict = exifDict
    ? { ...exifDict, "0th": { ...(exifDict["0th"] ?? {}) } }
    : { "0th": {}, Exif: {}, GPS: {}, Interop: {} };

  const zeroth = baseDict["0th"] as ExifIfdEntry;
  // Orientation = 1 (normal) im Output. Das ist korrekt, weil
  // createImageBitmap das Bild beim Dekodieren bereits visuell orientiert.
  // Würde der Original-Tag (z.B. 6 = "90° CW") erhalten bleiben, würden
  // EXIF-bewusste Apps das Bild ein zweites Mal rotieren -> falsche Anzeige.
  zeroth[PIEXIF.ImageIFD.Orientation] = 1;
  zeroth[PIEXIF.ImageIFD.XResolution] = [dpi, 1];
  zeroth[PIEXIF.ImageIFD.YResolution] = [dpi, 1];
  zeroth[PIEXIF.ImageIFD.ResolutionUnit] = 2; // 2 = inches

  // Eingebettetes Kamera-Vorschaubild entfernen: Es zeigt den ungeschnittenen
  // Originalausschnitt (nach dem 4:3-Zuschnitt also falsch) und kostet bis zu ~60 KB.
  baseDict["1st"] = {};
  baseDict.thumbnail = null;
  return baseDict;
}

// Bytes, die attachExifAndDpi dem JPEG hinzufügt (EXIF + APP1-Kopf). Wird vom
// 1-MB-Budget abgezogen, damit die fertige Datei sicher darunter bleibt.
export function exifOverheadBytes(exifDict: ExifDict | null, dpi: number): number {
  try {
    return PIEXIF.dump(buildExifDict(exifDict, dpi)).length + 4;
  } catch {
    return 65_536; // Maximalgröße eines APP1-Segments als sichere Annahme
  }
}

export function attachExifAndDpi(
  jpegBytes: Uint8Array,
  exifDict: ExifDict | null,
  dpi: number,
): Uint8Array {
  try {
    const exifBinary = PIEXIF.dump(buildExifDict(exifDict, dpi));
    const inputDataUrl = `data:image/jpeg;base64,${uint8ToBase64(jpegBytes)}`;
    const outputDataUrl = PIEXIF.insert(exifBinary, inputDataUrl);
    return base64ToUint8(outputDataUrl.split(",", 2)[1]);
  } catch (err) {
    // EXIF-Merge ist nice-to-have, nicht kritisch. Bei Fehler: Original-JPEG
    // ohne EXIF zurückgeben statt die ganze Pipeline scheitern zu lassen.
    console.warn("EXIF-Merge fehlgeschlagen:", err);
    return jpegBytes;
  }
}

function arrayBufferToBinaryString(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let result = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    result += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return result;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
